import { Location } from '@angular/common'
import { Injectable } from '@angular/core'
import { environment } from '../../../../environments/environment'
import { Venue } from '../../../domain/Venue'
import { WindowService } from '../../../services/window/window.service'
import { makeBookingQuery } from './venue-storefront-booking-mappers'
import type {
    VenueStorefrontBookingSearch,
    VenueStorefrontBookingSearchInitialValues,
} from './venue-storefront-booking-flow.types'

@Injectable({ providedIn: 'root' })
export class VenueStorefrontBookingNavigation {
    constructor(private windowService: WindowService, private location: Location) {}

    isOnPaymentDomain(): boolean {
        return [environment.dojoBookingBaseUrl, environment.teyaBookingBaseUrl].some(base =>
            this.windowService.getLocation().origin === new URL(base).origin
        )
    }

    enterBooking(venue: Venue, searchCriteria: VenueStorefrontBookingSearch): boolean {
        const url = this.paymentUrl(venue)
        if (url === null) {
            return false
        }
        const query = new URLSearchParams(makeBookingQuery(url.search, searchCriteria))

        // Browser Back must show the original form without immediately handing off again.
        query.set('ao', 'false')
        this.location.replaceState(this.windowService.getLocation().pathname, query.toString())
        query.set('ao', 'true')
        url.search = query.toString()
        this.windowService.navigateToUrl(url.toString())
        return true
    }

    resumeSetup(venue: Venue): boolean {
        const url = this.paymentUrl(venue)
        if (url === null) {
            return false
        }
        this.windowService.replaceLocation(url.toString())
        return true
    }

    returnToVenue(
        venue: Venue,
        searchCriteria: VenueStorefrontBookingSearchInitialValues | null = null
    ): boolean {
        if (!this.isOnPaymentDomain()) {
            return false
        }
        const current = this.windowService.getLocation()
        const url = new URL(venue.venueSlug ? environment.venueStorefrontBaseUrl : environment.landingPageBaseUrl)
        url.pathname = venue.venueSlug ? `/discover/${encodeURIComponent(venue.venueSlug)}` : current.pathname
        const query = new URLSearchParams(makeBookingQuery(current.search, searchCriteria))
        query.set('ao', 'false')
        url.search = query.toString()
        this.windowService.replaceLocation(url.toString())
        return true
    }

    private paymentUrl(venue: Venue): URL | null {
        const account = [venue.paymentsAccountForCapability('DepositPayments'),
            venue.paymentsAccountForCapability('CancellationChargePayments')]
            .find(account => account?.usesDojo() || account?.usesTeya())
        const base = account?.usesDojo() ? environment.dojoBookingBaseUrl
            : account?.usesTeya() ? environment.teyaBookingBaseUrl : null
        if (!base) {
            return null
        }
        const current = this.windowService.getLocation()
        const url = new URL(base)
        if (current.origin === url.origin) {
            return null
        }
        url.pathname = venue.venueSlug ? `/discover/${encodeURIComponent(venue.venueSlug)}` : current.pathname
        url.search = current.search
        url.hash = current.hash
        return url
    }
}
